<template>
  <b-modal
    :active="value"
    has-modal-card
    trap-focus
    @close="close"
  >
    <div class="modal-card warehouse-inventory-modal" style="width: 1180px; max-width: 98%;">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ isEditMode ? $t('edit_inventory_line_title') : $t('add_inventory_line_title') }}
        </p>
        <button type="button" class="delete" @click="close" />
      </header>
      <section class="modal-card-body">
        <b-loading :active="isCatalogsLoading" :is-full-page="false" />
        <div class="grid grid-cols-12 gap-x-4 gap-y-2">
          <div v-if="isEditingMaterial" class="col-span-12 md:col-span-2">
            <b-field custom-class="is-small" :label="$t('id')">
              <b-input size="is-small" :value="form.id != null ? String(form.id) : ''" disabled />
            </b-field>
          </div>
          <div :class="isEditingMaterial ? 'col-span-12 md:col-span-10' : 'col-span-12'">
            <b-field custom-class="is-small"
              required
              :type="formErrors.warehouse_material_id ? 'is-danger' : ''"
              :message="formErrors.warehouse_material_id ? $t('required_field') : ''"
            >
              <template #label>
                {{ $t('material') }} <span class="has-text-danger">*</span>
              </template>
              <b-autocomplete size="is-small"
                v-model="materialSearch"
                :data="materialOptions"
                :loading="isMaterialLoading"
                :custom-formatter="formatMaterialLabel"
                check-infinite-scroll
                open-on-focus
                clearable
                expanded
                append-to-body
                @typing="onMaterialTyping"
                @select="onMaterialSelected"
                @infinite-scroll="onMaterialInfiniteScroll"
              >
                <template #default="props">
                  {{ props.option.code }} - {{ props.option.description }}
                </template>
                <template #empty>{{ $t('no_results') }}</template>
              </b-autocomplete>
            </b-field>
          </div>

          <div class="col-span-12">
            <b-field custom-class="is-small" :label="$t('material_description')">
              <b-input size="is-small" v-model="form.material_description" type="textarea" rows="2" disabled />
            </b-field>
          </div>
          <div v-if="!isEditMode && !parentInventoryId" class="col-span-12">
            <b-field custom-class="is-small" :label="$t('packing_list_description')">
              <b-input size="is-small" v-model="form.packing_list_description" type="textarea" rows="2" maxlength="255" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-3">
            <b-field custom-class="is-small" :label="$t('category')">
              <warehouse-catalog-autocomplete size="is-small"
                append-to-body
                v-model="form.category"
                :project-id="normalizedProjectId"
                endpoint="taxonomies"
                response-key="taxonomies"
                order-field="order_column"
                value-field="name"
                :placeholder="$t('category')"
                :extra-params="taxonomyExtraParams('category')"
                :create-payload-builder="taxonomyPayloadBuilder('category')"
                create-on-missing
                :initial-option="form.category ? { name: form.category } : null"
              />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-3">
            <b-field custom-class="is-small" :label="$t('order_number')">
              <b-input size="is-small" v-model="form.order_number" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-4">
            <b-field custom-class="is-small" :label="$t('provider')">
              <b-select size="is-small" v-model="form.warehouse_provider_id" expanded>
                <option :value="null">-</option>
                <option v-for="p in providerOptions" :key="`p-${p.id}`" :value="p.id">
                  {{ (p.code ? `${p.code} - ` : '') + (p.name || '-') }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-2">
            <b-field custom-class="is-small" :label="$t('unit')">
              <b-select size="is-small" v-model="form.unit_id" expanded>
                <option :value="null">-</option>
                <option v-for="u in unitOptions" :key="`u-${u.id}`" :value="u.id">
                  {{ u.code || u.name || '-' }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-3">
            <b-field custom-class="is-small" :label="$t('drawing_number')">
              <b-input size="is-small" v-model="form.drawing_number" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-3">
            <b-field custom-class="is-small" :label="$t('tag_kks')">
              <b-input size="is-small" v-model="form.tag_kks" />
            </b-field>
          </div>

          <div class="col-span-12 md:col-span-3">
            <b-field custom-class="is-small"
              :required="!parentInventoryId"
              :type="formErrors.packing_list_number ? 'is-danger' : ''"
              :message="formErrors.packing_list_number ? $t('required_field') : ''"
            >
              <template #label>
                {{ $t('packing_list') }}
                <span v-if="!parentInventoryId" class="has-text-danger">*</span>
              </template>
              <b-input size="is-small" v-model="form.packing_list_number" :disabled="isEditMode" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-3">
            <b-field custom-class="is-small" :label="$t('bulk_number')">
              <b-input size="is-small" v-model="form.package_number" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-4">
            <b-field custom-class="is-small" :label="$t('bulk_type')">
              <warehouse-catalog-autocomplete size="is-small"
                append-to-body
                v-model="form.package_type"
                :project-id="normalizedProjectId"
                endpoint="taxonomies"
                response-key="taxonomies"
                order-field="order_column"
                value-field="name"
                :placeholder="$t('bulk_type')"
                :extra-params="taxonomyExtraParams('package_type')"
                :create-payload-builder="taxonomyPayloadBuilder('package_type')"
                create-on-missing
                :initial-option="form.package_type ? { name: form.package_type } : null"
              />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-2">
            <b-field custom-class="is-small" :label="$t('quantity')">
              <b-input size="is-small" v-model="form.received_quantity" type="number" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-2">
            <b-field custom-class="is-small" :label="$t('stackable')">
              <b-select size="is-small" v-model="form.stackable" expanded>
                <option :value="1">YES</option>
                <option :value="0">NO</option>
              </b-select>
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-4">
            <b-field custom-class="is-small"
              required
              :type="formErrors.warehouse_type ? 'is-danger' : ''"
              :message="formErrors.warehouse_type ? $t('required_field') : ''"
            >
              <template #label>
                {{ $t('warehouse_type') }} <span class="has-text-danger">*</span>
              </template>
              <warehouse-catalog-autocomplete size="is-small"
                append-to-body
                v-model="form.warehouse_type"
                :project-id="normalizedProjectId"
                endpoint="taxonomies"
                response-key="taxonomies"
                order-field="order_column"
                value-field="name"
                :placeholder="$t('warehouse_type')"
                :extra-params="taxonomyExtraParams('storage_type')"
                :create-payload-builder="taxonomyPayloadBuilder('storage_type')"
                create-on-missing
                :initial-option="form.warehouse_type ? { name: form.warehouse_type } : null"
              />
            </b-field>
          </div>

          <template v-if="!isEditMode">
            <div class="col-span-12 md:col-span-2">
              <b-field custom-class="is-small" :label="$t('length')">
                <b-input size="is-small" v-model="form.length_cm" type="number" step="0.01" />
              </b-field>
            </div>
            <div class="col-span-12 md:col-span-2">
              <b-field custom-class="is-small" :label="$t('width')">
                <b-input size="is-small" v-model="form.width_cm" type="number" step="0.01" />
              </b-field>
            </div>
            <div class="col-span-12 md:col-span-2">
              <b-field custom-class="is-small" :label="$t('height')">
                <b-input size="is-small" v-model="form.height_cm" type="number" step="0.01" />
              </b-field>
            </div>
            <div class="col-span-12 md:col-span-2">
              <b-field custom-class="is-small" :label="$t('volume')">
                <b-input size="is-small" v-model="form.volume_m3" type="number" step="0.01" />
              </b-field>
            </div>
            <div class="col-span-12 md:col-span-2">
              <b-field custom-class="is-small" :label="$t('net_weight_kg')">
                <b-input size="is-small" v-model="form.net_weight_kg" type="number" step="0.01" />
              </b-field>
            </div>
            <div class="col-span-12 md:col-span-2">
              <b-field custom-class="is-small" :label="$t('gross_weight_kg')">
                <b-input size="is-small" v-model="form.gross_weight_kg" type="number" step="0.01" />
              </b-field>
            </div>
          </template>

          <div class="col-span-12 md:col-span-4">
            <b-field custom-class="is-small" :label="$t('status')">
              <b-input size="is-small" v-model="form.status" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-4">
            <b-field custom-class="is-small" :label="$t('location')">
              <b-input size="is-small" v-model="form.location" />
            </b-field>
          </div>
          <div class="col-span-12 md:col-span-4">
            <b-field custom-class="is-small" :label="$t('storehouse')">
              <b-input size="is-small" v-model="form.warehouse" />
            </b-field>
          </div>
          <div class="col-span-12">
            <b-field custom-class="is-small" :label="$t('comments')">
              <b-input size="is-small" v-model="form.comments" type="textarea" rows="2" />
            </b-field>
          </div>

          <div v-if="hasStoreExtrafields" class="col-span-12">
            <hr class="my-3" />
            <h4 class="text-sm font-semibold text-blue-900 mb-3">{{ $t('extrafields') }}</h4>

            <div class="grid grid-cols-12 gap-x-4 gap-y-2">
              <div
                v-for="field in storeExtrafields"
                :key="`sef-${field.id}`"
                class="col-span-12 md:col-span-4"
              >
                <b-field custom-class="is-small" :label="field.name || field.code || '-'">
                  <b-select size="is-small"
                    v-if="Number(field.field_type) === STORE_EXTRAFIELD_TYPES.SELECT"
                    v-model="storeExtrafieldForm[field.id]"
                    expanded
                  >
                    <option :value="null">-</option>
                    <option
                      v-for="opt in (field.values || [])"
                      :key="`sef-${field.id}-opt-${opt.id}`"
                      :value="opt.id"
                    >
                      {{ opt.name || opt.code || `#${opt.id}` }}
                    </option>
                  </b-select>

                  <b-input size="is-small"
                    v-else-if="Number(field.field_type) === STORE_EXTRAFIELD_TYPES.CALENDAR"
                    v-model="storeExtrafieldForm[field.id]"
                    type="date"
                  />

                  <b-checkbox
                    v-else-if="Number(field.field_type) === STORE_EXTRAFIELD_TYPES.CHECKBOX"
                    v-model="storeExtrafieldForm[field.id]"
                    :true-value="1"
                    :false-value="0"
                  >
                    {{ $t('yes') }}
                  </b-checkbox>

                  <b-input size="is-small"
                    v-else
                    v-model="storeExtrafieldForm[field.id]"
                  />
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <v-button class="mr-3" @click="close" color="dark">
          {{ $t('cancel') }}
        </v-button>
        <v-button :loading="isSaving" color="green" icon="save" icon-pack="fas" @click="save">
          {{ $t('save') }}
        </v-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import WarehouseCatalogAutocomplete from './warehouse-catalog-autocomplete.vue'

const STORE_EXTRAFIELD_TYPES = Object.freeze({
  SELECT: 0,
  INPUT: 1,
  CALENDAR: 2,
  CHECKBOX: 3,
})

function emptyForm() {
  return {
    id: '',
    warehouse_material_id: null,
    material_description: '',
    category: '',
    order_number: '',
    warehouse_provider_id: null,
    unit_id: null,
    drawing_number: '',
    tag_kks: '',
    packing_list_number: '',
    packing_list_description: '',
    package_number: '',
    package_type: '',
    received_quantity: '',
    length_cm: '',
    width_cm: '',
    height_cm: '',
    volume_m3: '',
    net_weight_kg: '',
    gross_weight_kg: '',
    stackable: 0,
    warehouse_type: '',
    status: '',
    location: '',
    warehouse: '',
    comments: '',
  }
}

export default {
  name: 'warehouse-add-inventory-material-modal',
  components: { WarehouseCatalogAutocomplete },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: [Number, String],
      required: true,
    },
    /** Si viene, el material se crea bajo este inventario (bulto). Si es null, raíz del packing list indicado por número / respuesta. */
    parentInventoryId: {
      type: [Number, String],
      default: null,
    },
    /** Packing list abierto en detalle (para refrescar tabla tras guardar). */
    packingListId: {
      type: [Number, String],
      default: null,
    },
    presetPackingListNumber: {
      type: String,
      default: '',
    },
    isEditingMaterial: {
      type: Boolean,
      default: false,
    },
    /** Si viene, el modal carga el inventario y actúa como edición. */
    editInventoryId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      form: emptyForm(),
      materialOptions: [],
      materialSearch: '',
      materialSearchTerm: '',
      materialPage: 1,
      materialLastPage: 1,
      isMaterialLoading: false,
      materialSearchTimeout: null,
      providerOptions: [],
      unitOptions: [],
      storeExtrafields: [],
      storeExtrafieldForm: {},
      STORE_EXTRAFIELD_TYPES,
      isCatalogsLoading: false,
      isSaving: false,
      formErrors: {
        warehouse_material_id: false,
        packing_list_number: false,
        warehouse_type: false,
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
    normalizedProjectId() {
      return Number(this.projectId) || null
    },
    hasStoreExtrafields() {
      return Array.isArray(this.storeExtrafields) && this.storeExtrafields.length > 0
    },
    normalizedEditInventoryId() {
      const n = this.editInventoryId != null && this.editInventoryId !== '' ? Number(this.editInventoryId) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
    isEditMode() {
      return Boolean(this.normalizedEditInventoryId)
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetAndPrepare()
      } else {
        this.form = emptyForm()
        this.materialSearch = ''
      }
    },
    'form.warehouse_material_id': function () { this.formErrors.warehouse_material_id = false },
    'form.packing_list_number': function () { this.formErrors.packing_list_number = false },
    'form.warehouse_type': function () { this.formErrors.warehouse_type = false },
  },
  methods: {
    taxonomyExtraParams(type) {
      return { type, only_active: 1 }
    },
    taxonomyPayloadBuilder(type) {
      return (name) => ({ type, name })
    },
    close() {
      this.$emit('input', false)
    },
    async resetAndPrepare() {
      this.form = emptyForm()
      this.materialSearch = ''
      this.materialSearchTerm = ''
      this.materialPage = 1
      this.materialLastPage = 1
      this.storeExtrafields = []
      this.storeExtrafieldForm = {}
      if (this.presetPackingListNumber) {
        this.form.packing_list_number = String(this.presetPackingListNumber)
      }
      await this.loadCatalogs()
      if (this.isEditMode) {
        await this.loadInventoryForEdit()
        await this.loadInventoryExtrafieldsForEdit()
      }
    },
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    extractLastPageFromResponse(data) {
      const raw = (data && data.lastPage != null)
        ? data.lastPage
        : (data && data.data && data.data.lastPage != null ? data.data.lastPage : 1)
      const n = Number(raw)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    async fetchAllCatalogPages(url, params, key) {
      const perPage = 100
      const firstRes = await axios.get(url, { params: { ...params, per_page: perPage, page: 1 } })
      const items = [...this.extractArrayFromResponse(firstRes.data, key)]
      const lastPage = this.extractLastPageFromResponse(firstRes.data)
      if (lastPage <= 1) return items

      const requests = []
      for (let page = 2; page <= lastPage; page += 1) {
        requests.push(axios.get(url, { params: { ...params, per_page: perPage, page } }))
      }
      const responses = await Promise.all(requests)
      responses.forEach((res) => {
        items.push(...this.extractArrayFromResponse(res.data, key))
      })
      return items
    },
    extractInventoryLineFromSaveResponse(responseData) {
      if (!responseData) return null
      return responseData.inventory_line || responseData.inventory || null
    },
    isFormFieldEmpty(value) {
      return value == null || String(value).trim() === ''
    },
    formatMaterialLabel(material) {
      if (!material) return ''
      const code = material.code != null ? String(material.code) : ''
      const description = material.description != null ? String(material.description) : ''
      return description ? `${code} - ${description}` : code
    },
    async fetchMaterialsPage(term, page) {
      const params = { per_page: 100, page, order_field: 'code', order_dir: 'asc' }
      if (term) params.search = term
      const { data } = await axios.get(
        `/api/v2/project/${this.normalizedProjectId}/warehouse/materials`,
        { params },
      )
      return {
        items: this.extractArrayFromResponse(data, 'materials'),
        lastPage: this.extractLastPageFromResponse(data),
      }
    },
    async loadMaterialsFirstPage(term) {
      this.isMaterialLoading = true
      try {
        const { items, lastPage } = await this.fetchMaterialsPage(term, 1)
        this.materialOptions = items
        this.materialPage = 1
        this.materialLastPage = lastPage
        this.materialSearchTerm = term || ''
      } finally {
        this.isMaterialLoading = false
      }
    },
    onMaterialTyping(term) {
      this.form.warehouse_material_id = null
      clearTimeout(this.materialSearchTimeout)
      this.materialSearchTimeout = setTimeout(() => {
        this.loadMaterialsFirstPage((term || '').trim())
      }, 300)
    },
    async onMaterialInfiniteScroll() {
      if (this.isMaterialLoading || this.materialPage >= this.materialLastPage) return
      this.isMaterialLoading = true
      try {
        const nextPage = this.materialPage + 1
        const { items, lastPage } = await this.fetchMaterialsPage(this.materialSearchTerm, nextPage)
        this.materialOptions = this.materialOptions.concat(items)
        this.materialPage = nextPage
        this.materialLastPage = lastPage
      } finally {
        this.isMaterialLoading = false
      }
    },
    onMaterialSelected(material) {
      if (!material) {
        this.form.warehouse_material_id = null
        return
      }
      this.form.warehouse_material_id = Number(material.id)
      this.applyMaterialTemplateIfEmpty(material)
    },
    async loadSelectedMaterialLabel(materialId) {
      const id = materialId != null ? Number(materialId) : null
      if (!Number.isFinite(id) || id < 1) return

      const inList = (this.materialOptions || []).find((m) => Number(m.id) === id)
      if (inList) {
        this.materialSearch = this.formatMaterialLabel(inList)
        return
      }

      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.normalizedProjectId}/warehouse/materials`,
          { params: { warehouse_material_id: id, per_page: 1, page: 1 } },
        )
        const items = this.extractArrayFromResponse(data, 'materials')
        if (items.length > 0) {
          this.materialSearch = this.formatMaterialLabel(items[0])
        }
      } catch (e) {
        console.error(e)
      }
    },
    applyMaterialTemplateIfEmpty(material) {
      if (!material) return

      this.form.material_description = material.description != null ? String(material.description) : ''

      if (this.isFormFieldEmpty(this.form.category) && material.category != null) {
        this.form.category = String(material.category)
      }
      if (this.isFormFieldEmpty(this.form.order_number) && material.order_number != null) {
        this.form.order_number = String(material.order_number)
      }
      if (this.form.warehouse_provider_id == null && material.warehouse_provider_id != null) {
        this.form.warehouse_provider_id = Number(material.warehouse_provider_id)
      }
      if (this.form.unit_id == null && material.unit_id != null) {
        this.form.unit_id = Number(material.unit_id)
      }
      if (this.isFormFieldEmpty(this.form.drawing_number) && material.drawing_number != null) {
        this.form.drawing_number = String(material.drawing_number)
      }
      if (this.isFormFieldEmpty(this.form.tag_kks) && material.tag_kks != null) {
        this.form.tag_kks = String(material.tag_kks)
      }
    },
    async loadCatalogs() {
      if (!this.normalizedProjectId) return

      this.isCatalogsLoading = true
      try {
        const companyId = this.company && this.company.id ? Number(this.company.id) : null
        const [, providers, unitsRes, extrafieldsRes] = await Promise.all([
          this.loadMaterialsFirstPage(''),
          this.fetchAllCatalogPages(
            `/api/v2/project/${this.normalizedProjectId}/warehouse/providers`,
            { order_field: 'name', order_dir: 'asc' },
            'providers',
          ),
          companyId
            ? axios.get(`/api/v2/company/${companyId}/measurement-units`, { params: { per_page: 300 } })
            : Promise.resolve({ data: {} }),
          axios.get(`/api/v2/project/${this.normalizedProjectId}/store-extrafields`),
        ])

        this.providerOptions = providers
        this.unitOptions = this.extractArrayFromResponse(unitsRes.data, 'units')

        const extrafields = this.extractArrayFromResponse(extrafieldsRes.data, 'extrafields')
        this.storeExtrafields = Array.isArray(extrafields) ? extrafields : []
        this.storeExtrafieldForm = (this.storeExtrafields || []).reduce((acc, f) => {
          const id = f && f.id != null ? Number(f.id) : null
          if (!Number.isFinite(id)) return acc
          acc[id] = Number(f.field_type) === STORE_EXTRAFIELD_TYPES.CHECKBOX ? 0 : null
          return acc
        }, {})
      } finally {
        this.isCatalogsLoading = false
      }
    },
    async loadInventoryForEdit() {
      if (!this.normalizedProjectId || !this.normalizedEditInventoryId) return

      this.isCatalogsLoading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.normalizedProjectId}/warehouse/inventory/${this.normalizedEditInventoryId}`,
        )
        const line = data && data.inventory_line ? data.inventory_line : null
        if (!line) return

        this.form = {
          ...emptyForm(),
          id: line.id != null ? String(line.id) : '',
          warehouse_material_id: line.warehouse_material_id != null ? Number(line.warehouse_material_id) : null,
          material_description: line.material_description != null ? String(line.material_description) : '',
          category: line.category != null ? String(line.category) : '',
          order_number: line.order_number != null ? String(line.order_number) : '',
          warehouse_provider_id: line.warehouse_provider_id != null ? Number(line.warehouse_provider_id) : null,
          unit_id: line.unit_id != null ? Number(line.unit_id) : null,
          drawing_number: line.drawing_number != null ? String(line.drawing_number) : '',
          tag_kks: line.tag_kks != null ? String(line.tag_kks) : '',
          packing_list_number: line.packing_list_number != null ? String(line.packing_list_number) : '',
          package_number: line.package_number != null ? String(line.package_number) : '',
          package_type: line.package_type != null ? String(line.package_type) : '',
          received_quantity: line.received_quantity != null ? String(line.received_quantity) : '',
          length_cm: line.length_cm != null ? String(line.length_cm) : '',
          width_cm: line.width_cm != null ? String(line.width_cm) : '',
          height_cm: line.height_cm != null ? String(line.height_cm) : '',
          volume_m3: line.volume_m3 != null ? String(line.volume_m3) : '',
          net_weight_kg: line.net_weight_kg != null ? String(line.net_weight_kg) : '',
          gross_weight_kg: line.gross_weight_kg != null ? String(line.gross_weight_kg) : '',
          stackable: line.stackable != null ? Number(line.stackable) : 0,
          warehouse_type: line.warehouse_type != null ? String(line.warehouse_type) : '',
          status: line.status != null ? String(line.status) : '',
          location: line.location != null ? String(line.location) : '',
          warehouse: line.warehouse != null ? String(line.warehouse) : '',
          comments: line.comments != null ? String(line.comments) : '',
        }

        await this.loadSelectedMaterialLabel(this.form.warehouse_material_id)
      } catch (e) {
        console.error(e)
      } finally {
        this.isCatalogsLoading = false
      }
    },
    async loadInventoryExtrafieldsForEdit() {
      if (!this.normalizedProjectId || !this.normalizedEditInventoryId || !this.hasStoreExtrafields) return

      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.normalizedProjectId}/warehouse/inventory/${this.normalizedEditInventoryId}/extrafields`,
        )
        const assignments = (data && Array.isArray(data.values)) ? data.values : []
        const byFieldId = assignments.reduce((acc, a) => {
          const id = a && a.extrafield_id != null ? Number(a.extrafield_id) : null
          if (!Number.isFinite(id) || id < 1) return acc
          acc[id] = a
          return acc
        }, {})

        this.storeExtrafieldForm = (this.storeExtrafields || []).reduce((acc, f) => {
          const id = f && f.id != null ? Number(f.id) : null
          if (!Number.isFinite(id)) return acc
          const fieldType = f && f.field_type != null ? Number(f.field_type) : null
          const current = byFieldId[id]

          if (fieldType === STORE_EXTRAFIELD_TYPES.SELECT) {
            acc[id] = current && current.value_id != null ? Number(current.value_id) : null
            return acc
          }

          if (fieldType === STORE_EXTRAFIELD_TYPES.CHECKBOX) {
            const raw = current && current.value_free != null ? current.value_free : null
            acc[id] = raw === 1 || raw === '1' || raw === true ? 1 : 0
            return acc
          }

          acc[id] = current && current.value_free != null ? String(current.value_free) : null
          return acc
        }, {})
      } catch (e) {
        console.error(e)
      }
    },
    buildInventoryPayload() {
      const parentId = this.parentInventoryId != null && this.parentInventoryId !== ''
        ? Number(this.parentInventoryId)
        : null

      return {
        warehouse_material_id: this.form.warehouse_material_id != null
          ? Number(this.form.warehouse_material_id)
          : null,
        material_description: this.form.material_description || null,
        category: this.form.category || null,
        order_number: this.form.order_number || null,
        warehouse_provider_id: this.form.warehouse_provider_id != null
          ? Number(this.form.warehouse_provider_id)
          : null,
        unit_id: this.form.unit_id != null ? Number(this.form.unit_id) : null,
        drawing_number: this.form.drawing_number || null,
        tag_kks: this.form.tag_kks || null,
        packing_list_number: this.form.packing_list_number || null,
        packing_list_description: this.form.packing_list_description || null,
        package_number: this.form.package_number || null,
        package_type: this.form.package_type || null,
        received_quantity: this.form.received_quantity !== '' ? this.form.received_quantity : null,
        length_cm: this.form.length_cm !== '' ? this.form.length_cm : null,
        width_cm: this.form.width_cm !== '' ? this.form.width_cm : null,
        height_cm: this.form.height_cm !== '' ? this.form.height_cm : null,
        volume_m3: this.form.volume_m3 !== '' ? this.form.volume_m3 : null,
        net_weight_kg: this.form.net_weight_kg !== '' ? this.form.net_weight_kg : null,
        gross_weight_kg: this.form.gross_weight_kg !== '' ? this.form.gross_weight_kg : null,
        stackable: this.form.stackable != null ? Number(this.form.stackable) : 0,
        warehouse_type: this.form.warehouse_type || null,
        status: this.form.status || null,
        location: this.form.location || null,
        warehouse: this.form.warehouse || null,
        comments: this.form.comments || null,
        parent_id: parentId && parentId > 0 ? parentId : null,
      }
    },
    buildWarehouseInventoryExtrafieldsPayload() {
      if (!Array.isArray(this.storeExtrafields) || this.storeExtrafields.length === 0) return []
      const form = this.storeExtrafieldForm && typeof this.storeExtrafieldForm === 'object'
        ? this.storeExtrafieldForm
        : {}

      return this.storeExtrafields
        .map((field) => {
          const extrafieldId = field && field.id != null ? Number(field.id) : null
          if (!Number.isFinite(extrafieldId) || extrafieldId < 1) return null

          const fieldType = field && field.field_type != null ? Number(field.field_type) : null
          const raw = form[extrafieldId]

          if (fieldType === STORE_EXTRAFIELD_TYPES.SELECT) {
            const valueId = raw != null && raw !== '' ? Number(raw) : null
            return {
              extrafield_id: extrafieldId,
              value_id: Number.isFinite(valueId) && valueId > 0 ? valueId : null,
              value_free: null,
            }
          }

          // For non-select types we store everything as value_free (string/number/date/checkbox 0/1)
          if (raw == null || raw === '') {
            return {
              extrafield_id: extrafieldId,
              value_id: null,
              value_free: null,
            }
          }

          return {
            extrafield_id: extrafieldId,
            value_id: null,
            value_free: raw,
          }
        })
        .filter(Boolean)
    },
    async save() {
      if (!this.normalizedProjectId) return

      const errors = {
        warehouse_material_id: !this.form.warehouse_material_id,
        packing_list_number: !this.parentInventoryId
          && !(this.form.packing_list_number && String(this.form.packing_list_number).trim()),
        warehouse_type: !(this.form.warehouse_type && String(this.form.warehouse_type).trim()),
      }
      this.formErrors = errors
      if (errors.warehouse_material_id || errors.packing_list_number || errors.warehouse_type) {
        return
      }

      this.isSaving = true
      try {
        const payload = this.buildInventoryPayload()

        const response = this.isEditMode
          ? await axios.put(
            `/api/v2/project/${this.normalizedProjectId}/warehouse/inventory/${this.normalizedEditInventoryId}`,
            payload,
          )
          : await axios.post(
            `/api/v2/project/${this.normalizedProjectId}/warehouse/inventory`,
            payload,
          )

        const responseData = response && response.data ? response.data : null

        const line = this.extractInventoryLineFromSaveResponse(responseData)
        const inventoryId = this.isEditMode
          ? this.normalizedEditInventoryId
          : (line && line.id != null ? Number(line.id) : null)

        // Save extrafield values for this inventory (only after we have an inventory_id)
        if (Number.isFinite(inventoryId) && inventoryId > 0 && this.hasStoreExtrafields) {
          const values = this.buildWarehouseInventoryExtrafieldsPayload()
          if (values.length > 0) {
            try {
              await axios.post(
                `/api/v2/project/${this.normalizedProjectId}/warehouse/inventory/${inventoryId}/extrafields`,
                { values },
              )
            } catch (e) {
              if (this.$notify) {
                this.$notify.error('save_error')
              }
              console.error(e)
            }
          }
        }

        if (this.$notify) {
          this.$notify.success('save_successful')
        }

        let resolvedPackingListId = null
        if (line && line.warehouse_packing_list_id != null) {
          resolvedPackingListId = Number(line.warehouse_packing_list_id)
        } else if (this.packingListId != null && this.packingListId !== '') {
          resolvedPackingListId = Number(this.packingListId)
        }

        this.$emit('saved', { packingListId: resolvedPackingListId })
        this.$emit('input', false)
      } catch (e) {
        if (this.$notify) {
          this.$notify.error('save_error')
        }
        this.$emit('error', e)
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
.warehouse-inventory-modal ::v-deep .field > .label {
  min-height: 2.6em;
  display: flex;
  align-items: flex-end;
  margin-bottom: 0.25rem;
}
</style>
